import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Base_URL } from "../url";
// ─── Mock auth hook – replace with your real one ──────────────────────────────
import { useAuth } from "../Context/AuthContext";

// ─── API helper ───────────────────────────────────────────────────────────────
// const API_BASE = import.meta.env?.VITE_API_BASE ?? "";

async function fetchProductionPlans(organisationId) {
  const res = await fetch(
    `${Base_URL}/ProductionStation/GetProductionPlans?organisationId=${organisationId}`
  );
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  return res.json();
}

// ─── Icons (inline SVG, no extra deps) ───────────────────────────────────────
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FileIcon = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProductionPlanList() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!user?.organisationId) return;
    setLoading(true);
    fetchProductionPlans(user.organisationId)
      .then(setPlans)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [user?.organisationId]);

  const filtered = plans.filter(
    (p) =>
      p.productionPlanId?.toLowerCase().includes(search.toLowerCase()) ||
      p.productionPlanDesc?.toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (productionPlanId) => {
    navigate(`/editProductionPlan/${productionPlanId}`);
  };

  return (
    <>
      <style>{`
       /* ===========================
   Production Plan List Theme
   =========================== */

@import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.ppl-root {
  font-family: 'Sora', sans-serif;
  min-height: 100vh;
  background: #f8fafc;
  color: #1f2937;
  padding: 24px;
}

/* ===========================
   Header
   =========================== */

.ppl-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.ppl-title-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ppl-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #6366f1;
}

.ppl-title {
  font-size: 28px;
  font-weight: 700;
  color: #6366f1;
  line-height: 1.2;
}

.ppl-subtitle {
  font-size: 13px;
  color: #6b7280;
}

/* ===========================
   Search
   =========================== */

.ppl-search-wrap {
  position: relative;
  width: 300px;
}

.ppl-search-wrap svg {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.ppl-search {
  width: 100%;
  height: 42px;
  padding: 0 14px 0 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  outline: none;
  transition: all 0.2s ease;
}

.ppl-search::placeholder {
  color: #9ca3af;
}

.ppl-search:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* ===========================
   Stats
   =========================== */

.ppl-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.ppl-stat-pill {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ppl-stat-pill strong {
  color: #6366f1;
  font-size: 16px;
  font-weight: 700;
}

/* ===========================
   Card
   =========================== */

.ppl-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

/* ===========================
   Table
   =========================== */

.ppl-table {
  width: 100%;
  border-collapse: collapse;
}

.ppl-table thead tr {
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.ppl-table th {
  padding: 14px 20px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6b7280;
}

.ppl-table th:last-child,
.ppl-table td:last-child {
  text-align: center;
}

.ppl-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.ppl-table tbody tr:last-child {
  border-bottom: none;
}

.ppl-table tbody tr:hover {
  background: #f8fafc;
}

.ppl-table td {
  padding: 14px 20px;
  font-size: 14px;
  color: #374151;
}

/* ===========================
   Row Number
   =========================== */

.ppl-row-num {
  width: 50px;
  color: #9ca3af;
  font-size: 12px;
  font-family: 'JetBrains Mono', monospace;
}

/* ===========================
   Plan ID
   =========================== */

.ppl-id {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #6366f1;
  font-size: 12px;
  font-weight: 600;
  font-family: 'JetBrains Mono', monospace;
}

/* ===========================
   Plan Name
   =========================== */

.ppl-name {
  font-weight: 500;
  color: #111827;
}

/* ===========================
   Edit Button
   =========================== */

.ppl-btn-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #6366f1;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ppl-btn-edit:hover {
  background: #4f46e5;
  transform: translateY(-1px);
}

.ppl-btn-edit:active {
  transform: translateY(0);
}

/* ===========================
   Empty State
   =========================== */

.ppl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  gap: 12px;
}

.ppl-empty-icon {
  color: #6366f1;
  opacity: 0.7;
}

.ppl-empty p {
  font-size: 15px;
  color: #374151;
}

.ppl-empty span {
  font-size: 13px;
  color: #9ca3af;
}

/* ===========================
   Skeleton Loader
   =========================== */

@keyframes shimmer {
  0% {
    background-position: -600px 0;
  }
  100% {
    background-position: 600px 0;
  }
}

.ppl-skeleton {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #f3f4f6 25%,
    #e5e7eb 50%,
    #f3f4f6 75%
  );
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
}

.ppl-skeleton-row td {
  padding: 18px 20px;
}

/* ===========================
   Error
   =========================== */

.ppl-error {
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
}

/* ===========================
   Footer
   =========================== */

.ppl-footer {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  color: #6b7280;
  font-size: 12px;
}

/* ===========================
   Responsive
   =========================== */

@media (max-width: 768px) {
  .ppl-header {
    flex-direction: column;
    align-items: stretch;
  }

  .ppl-search-wrap {
    width: 100%;
  }
}

@media (max-width: 640px) {
  .ppl-title {
    font-size: 22px;
  }

  .ppl-table th:first-child,
  .ppl-table td:first-child {
    display: none;
  }
}
      `}</style>

      <div className="ppl-root">
        {/* ── Page header ── */}
        <div className="ppl-header">
          <div className="ppl-title-group">
            <h1 className="ppl-title">Production Plans</h1>
            <p className="ppl-subtitle">Manage and edit your organisation's production plans</p>
          </div>
          <div className="ppl-search-wrap">
            <SearchIcon />
            <input
              className="ppl-search"
              type="text"
              placeholder="Search by ID or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* ── Stats bar ── */}
        {!loading && !error && (
          <div className="ppl-stats">
            <div className="ppl-stat-pill">
              <strong>{plans.length}</strong> Total plans
            </div>
            {search && (
              <div className="ppl-stat-pill">
                <strong>{filtered.length}</strong> Matching "{search}"
              </div>
            )}
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="ppl-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Failed to load production plans: {error}
          </div>
        )}

        {/* ── Table card ── */}
        {!error && (
          <div className="ppl-card">
            <table className="ppl-table">
              <thead>
                <tr>
                  <th className="ppl-row-num">#</th>
                  <th>Plan ID</th>
                  <th>Plan Name</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <tr key={i} className="ppl-skeleton-row">
                        <td><div className="ppl-skeleton" style={{ width: 24 }} /></td>
                        <td><div className="ppl-skeleton" style={{ width: 80 }} /></td>
                        <td><div className="ppl-skeleton" style={{ width: 220 }} /></td>
                        <td style={{ textAlign: "center" }}>
                          <div className="ppl-skeleton" style={{ width: 72, margin: "0 auto" }} />
                        </td>
                      </tr>
                    ))
                  : filtered.length === 0
                  ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="ppl-empty">
                            <span className="ppl-empty-icon"><FileIcon /></span>
                            <p>
                              {search
                                ? `No plans match "${search}"`
                                : "No production plans found"}
                            </p>
                            <span>
                              {search
                                ? "Try a different search term"
                                : "Production plans will appear here once created"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    )
                  : filtered.map((plan, idx) => (
                      <tr key={plan.productionPlanId}>
                        <td className="ppl-row-num">{idx + 1}</td>
                        <td>
                          <span className="ppl-id">{plan.productionPlanId}</span>
                        </td>
                        <td>
                          <span className="ppl-name">
                            {plan.productionPlanDesc || "—"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="ppl-btn-edit"
                            onClick={() => handleEdit(plan.productionPlanId)}
                            title={`Edit ${plan.productionPlanId}`}
                          >
                            <EditIcon /> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Footer ── */}
        {!loading && !error && filtered.length > 0 && (
          <div className="ppl-footer">
            <span>Showing {filtered.length} of {plans.length} plans</span>
            <span>Org ID: {user?.organisationId}</span>
          </div>
        )}
      </div>
    </>
  );
}